/**
 * SixMinuteWalkPanel — iter1052
 * Тест „6-минутно ходене": сатурация и пулс преди, таймер 6 минути,
 * сатурация, пулс и задух веднага след — и история на промяната.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Timer, Play, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DUR = 360;

export const SixMinuteWalkPanel = ({ isDark, onSaved }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [phase, setPhase] = useState("before");
  const [left, setLeft] = useState(DUR);
  const [form, setForm] = useState({});
  const tick = useRef(null);

  useEffect(() => {
    axios.get(`${API}/respiratory/walk-test`, { timeout: 25000 })
      .then(({ data: d }) => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => tick.current && clearInterval(tick.current);
  }, []);

  const start = () => {
    setPhase("walking");
    setLeft(DUR);
    tick.current = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) {
          clearInterval(tick.current);
          setPhase("after");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const stopEarly = () => {
    tick.current && clearInterval(tick.current);
    setForm((f) => ({ ...f, stopped: true, duration_sec: DUR - left }));
    setPhase("after");
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { stopped: !!form.stopped, duration_sec: form.duration_sec ?? DUR };
      ["spo2_before", "hr_before", "spo2_after", "hr_after", "hr_1min_after", "borg_0_10", "distance_m"].forEach((k) => {
        if (form[k] !== "" && form[k] != null) payload[k] = Number(form[k]);
      });
      const { data: d } = await axios.post(`${API}/respiratory/walk-test`, payload);
      setData(d);
      setPhase("before");
      setForm({});
      onSaved && onSaved();
    } finally { setSaving(false); }
  };

  const num = (k, lbl, testid) => (
    <label className="text-[10px] opacity-80">
      {lbl}
      <input type="number" step="any" value={form[k] ?? ""}
             onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
             data-testid={testid}
             className={`w-full mt-0.5 px-2 py-1 rounded-lg text-[11px] ${
               isDark ? "bg-slate-900 border border-slate-700" : "bg-white border border-slate-300"
             }`} />
    </label>
  );

  if (loading) {
    return <div className="p-3" data-testid="walk-loading">
      <Loader2 className="w-4 h-4 animate-spin text-sky-500" />
    </div>;
  }
  if (!data) return null;
  const box = isDark ? "bg-slate-900/60" : "bg-white border border-slate-200";
  const mm = String(Math.floor(left / 60)).padStart(1, "0");
  const ss = String(left % 60).padStart(2, "0");

  return (
    <div className={`mt-2 p-3 rounded-xl border-2 ${
      isDark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"
    }`} data-testid="walk-panel">
      <div className="flex items-center gap-2 mb-2">
        <Timer className="w-4 h-4 text-sky-500" />
        <div className="text-xs font-bold">Тест „6-минутно ходене"</div>
      </div>

      <ol className={`p-2.5 rounded-lg text-[10px] mb-2 space-y-0.5 ${box}`} data-testid="walk-instructions">
        {(data.instructions || []).map((s, i) => <li key={s}>{i + 1}. {s}</li>)}
      </ol>

      {phase === "before" && (
        <div className="space-y-2" data-testid="walk-phase-before">
          <div className="grid grid-cols-2 gap-2">
            {num("spo2_before", "SpO₂ в покой (%)", "walk-input-spo2-before")}
            {num("hr_before", "Пулс в покой", "walk-input-hr-before")}
          </div>
          <button onClick={start} data-testid="walk-start"
                  className="w-full py-2 rounded-lg bg-sky-600 text-white text-[11px] font-bold flex items-center justify-center gap-1">
            <Play className="w-3.5 h-3.5" /> Старт на 6 минути
          </button>
        </div>
      )}

      {phase === "walking" && (
        <div className="text-center py-3" data-testid="walk-phase-walking">
          <div className="text-4xl font-extrabold tabular-nums" data-testid="walk-timer">{mm}:{ss}</div>
          <p className="text-[10px] opacity-70 mt-1">Вървете с максималното темпо, което можете да поддържате.</p>
          <button onClick={stopEarly} data-testid="walk-stop"
                  className={`mt-2 px-3 py-1.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1 mx-auto ${
                    isDark ? "bg-slate-800 text-slate-200" : "bg-white border border-slate-300"
                  }`}>
            <Square className="w-3 h-3" /> Спрях по-рано
          </button>
        </div>
      )}

      {phase === "after" && (
        <div className="space-y-2" data-testid="walk-phase-after">
          <div className="text-[10px] font-bold opacity-80">Измерете веднага, преди да седнете</div>
          <div className="grid grid-cols-2 gap-2">
            {num("spo2_after", "SpO₂ след (%)", "walk-input-spo2-after")}
            {num("hr_after", "Пулс след", "walk-input-hr-after")}
            {num("hr_1min_after", "Пулс на 1-вата мин след", "walk-input-hr-1min")}
            {num("borg_0_10", "Задух 0–10", "walk-input-borg")}
            {num("distance_m", "Разстояние (м, ако знаете)", "walk-input-distance")}
          </div>
          <button onClick={save} disabled={saving} data-testid="walk-save"
                  className="w-full py-2 rounded-lg bg-sky-600 text-white text-[11px] font-bold disabled:opacity-60">
            {saving ? "Записвам…" : "Запиши резултата"}
          </button>
        </div>
      )}

      {data.latest && data.latest.verdict && (
        <div className={`mt-2 p-2.5 rounded-lg text-[10px] ${
          data.latest.verdict.level === "suspect"
            ? (isDark ? "bg-rose-900/30 border border-rose-500/50" : "bg-rose-50 border border-rose-300")
            : box
        }`} data-testid="walk-verdict">
          <div className="font-bold">{data.latest.verdict.label_bg}</div>
          <p className="opacity-90 mt-0.5">{data.latest.verdict.text_bg}</p>
        </div>
      )}

      {data.trend && (
        <div className={`mt-2 p-2.5 rounded-lg text-[10px] ${box}`} data-testid="walk-trend">
          <div className="font-bold opacity-80 mb-0.5">Промяна във времето</div>
          {data.trend.text_bg && <div>{data.trend.text_bg}</div>}
          {data.trend.distance_text_bg && <div>{data.trend.distance_text_bg}</div>}
        </div>
      )}

      {(data.history || []).length > 0 && (
        <div className={`mt-2 p-2.5 rounded-lg text-[10px] ${box}`} data-testid="walk-history">
          <div className="font-bold opacity-80 mb-1">История ({data.history.length})</div>
          {data.history.map((h, i) => (
            <div key={h.tested_at} data-testid={`walk-history-${i}`} className="flex gap-2">
              <span className="opacity-60">{String(h.tested_at).slice(0, 10)}</span>
              <span>{h.spo2_before ?? "—"}% → <strong>{h.spo2_after ?? "—"}%</strong></span>
              {h.distance_m ? <span className="opacity-70">{h.distance_m} м</span> : null}
              {h.borg_0_10 != null ? <span className="opacity-70">задух {h.borg_0_10}</span> : null}
            </div>
          ))}
        </div>
      )}

      <p className="text-[10px] italic opacity-70 mt-2" data-testid="walk-safety">{data.safety_bg}</p>
    </div>
  );
};

export default SixMinuteWalkPanel;
